import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatINR } from "../lib/api";
import SearchBar from "../components/SearchBar";
import { ArrowRight } from "lucide-react";

export default function SearchResults() {
    const [params] = useSearchParams();
    const q = params.get("q") || "";
    const [data, setData] = useState({ products: [], categories: [], total: 0 });
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!q) return;
        setLoading(true);
        api.get("/products/search", { params: { q } }).then(r => setData(r.data)).finally(() => setLoading(false));
    }, [q]);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="search-page">
            <div className="label-eyebrow text-sky-700">Search</div>
            <h1 className="font-serif text-4xl text-stone-900 mt-2 mb-6">Results for "{q}"</h1>
            <div className="max-w-2xl mb-10"><SearchBar size="md" testId="search-page-bar" /></div>

            {loading ? <p className="text-sm text-stone-500">Searching…</p> : (
                <>
                    {data.categories.length > 0 && (
                        <div className="mb-8">
                            <div className="label-eyebrow text-stone-500 mb-3">Matching categories</div>
                            <div className="flex flex-wrap gap-2">
                                {data.categories.map(c => (
                                    <Link key={c.id} to={`/shop?category=${c.id}`} className="text-sm border border-stone-300 px-4 py-2 hover:border-sky-700 hover:text-sky-700" data-testid={`sr-cat-${c.id}`}>{c.name}</Link>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="label-eyebrow text-stone-500 mb-3">{data.total} medicine{data.total === 1 ? "" : "s"} found</div>
                    {data.products.length === 0 ? (
                        <div className="border border-stone-200 bg-white p-12 text-center">
                            <h3 className="font-serif text-2xl text-stone-900">Nothing matched</h3>
                            <p className="text-sm text-stone-600 mt-2">Try a symptom (headache, fever) or category (mother tincture, biochemic).</p>
                            <Link to="/ai-doctor" className="inline-flex items-center gap-1 text-sky-700 mt-4 text-sm">Try the AI Doctor <ArrowRight className="w-3 h-3" /></Link>
                        </div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {data.products.map(p => (
                                <Link key={p.id} to={`/product/${p.slug}`} className="bg-white border border-stone-200 group" data-testid={`sr-product-${p.slug}`}>
                                    <div className="aspect-square bg-stone-100 overflow-hidden">
                                        {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                                    </div>
                                    <div className="p-4">
                                        <h3 className="font-serif text-lg text-stone-900 group-hover:text-sky-700">{p.name}</h3>
                                        <p className="text-xs text-stone-500 mt-1 line-clamp-2">{p.indications || p.short_description}</p>
                                        {p.variants?.[0] && <div className="text-sm text-sky-700 mt-2">From {formatINR(p.variants[0].price)}</div>}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
